import React, { useContext } from "react";
import { HotelIcon, TrendingDown, TrendingUp } from "lucide-react";
import { ThemeContext } from "../theme";
import { LanguageContext } from "../LanguageContext";

import WelcomeCard from "../../components/WelcomeCard";
import StatsGrid from "../../components/StatsGrid";
import DepartmentChart from "./components/DepartmentChart";
import ActivityFeed from "./components/ActivityFeed";

import useDashboardData from "./hooks/useDashboardData";
import useScreenSize from "./hooks/useScreenSize";

import {
  getResponsiveStyles,
  getHoverStyles,
  getDefaultStyles,
} from "../../utils/styleUtils";
import { text } from "./constants/text";
import * as icons from "../../constant/icons";

const Dashboard = ({
  employeeCount: propEmployeeCount,
  counts: propCounts,
  departmentStats: propDepartmentStats,
} = {}) => {
  const { theme } = useContext(ThemeContext);
  const { language } = useContext(LanguageContext);
  const BASE_URL = process.env.REACT_APP_BASE_URL;

  const screenSize = useScreenSize();

  const {
    employeeCount,
    counts,
    departmentStats,
    userName,
    userArabicName,
    loading,
    error,
    recentActivities,
  } = useDashboardData(
    propEmployeeCount,
    propCounts,
    propDepartmentStats,
    BASE_URL,
  );

  const stats = [
    {
      title: text[language].totalEmployees,
      value: employeeCount,
      valueAlign: 50,
      icon: icons.Users,
      iconBackground: "rgb(0, 180, 72)", // Tailwind's green-500 at 12% opacity
      //color: "bg-green-500",
      //cardHeight: "150px",
      //cardWidth: "250px",
      showSymbol: true,
      valueAlign: 50, // 12px from left edge of card
      //cardStyle: { borderRadius: "12px" }
    },
    {
      title: text[language].activeEmployees,
      value: counts.Active,
      description: text[language].thisWeek,
      //color: "bg-blue-500",
      icon: icons.UserCheck,
      valueAlign: 50, // 12px from left edge of card

      iconBackground: "rgb(6, 0, 180)", // Tailwind's green-500 at 12% opacity
    },
    {
      title: text[language].onLeave,
      value: counts.OnLeave,
      description: text[language].today,
      icon: icons.Calendar,
            valueAlign: 50, // 12px from left edge of card

      iconBackground: "rgb(180, 0, 72)", // Tailwind's red-500 at 12% opacity
    },
    {
      title: text[language].totalCompanies,
      value: counts.TotalCompanies,
      description: text[language].ovearall,
      icon: icons.Building,
            valueAlign: 50, // 12px from left edge of card

      iconBackground: "rgb(180, 57, 0)", // Tailwind's red-500 at 12% opacity
    },
  ];

  const styles = getResponsiveStyles(screenSize, theme, language);

  const departmentTotal = departmentStats.reduce(
    (sum, dept) => sum + Number(dept.count || 0),
    0,
  );
  const totalAcrossDepartments =
    Number(employeeCount || 0) > 0
      ? Number(employeeCount || 0)
      : departmentTotal;

  // Use Arabic name for Arabic language, English name for other languages
  const getDisplayName = () => {
    if (language === "ar") {
      // Try userArabicName first, then localStorage, then userName
      return (
        userArabicName ||
        localStorage.getItem("employeeArabicName") ||
        userName ||
        ""
      );
    } else {
      // English: use userName or localStorage
      return userName || localStorage.getItem("employeeName") || "";
    }
  };

  const displayName = getDisplayName();
  const formatTwoNames = (name = "") => {
    const parts = String(name).trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) return "";
    if (parts.length === 1) return parts[0];
    return `${parts[0]} ${parts[parts.length - 1]}`;
  };
  const displayNameShort = formatTwoNames(displayName);
  const displayTitle = displayNameShort
    ? `${text[language].welcome}, ${displayNameShort}`
    : text[language].welcome;

  // if (loading) {
  //   return (
  //     <div style={{
  //       ...styles.container,
  //       display: "flex",
  //       justifyContent: "center",
  //       alignItems: "center",
  //       minHeight: "400px"
  //     }}>
  //       <p style={{ color: theme === "dark" ? "#fff" : "#1a1a1a" }}>
  //         {language === "ar" ? "جاري التحميل..." : "Loading..."}
  //       </p>
  //     </div>
  //   );
  // }

  if (error) {
    return (
      <div
        style={{
          ...styles.container,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "400px",
        }}
      >
        <p style={{ color: "#ef4444" }}>
          {language === "ar" ? "حدث خطأ: " : "Error: "}
          {error}
        </p>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <WelcomeCard
        styles={styles}
        text={text}
        language={language}
        userName={displayNameShort}
        title={displayTitle}
      />

      <StatsGrid styles={styles} stats={stats} screenSize={screenSize} />

      <div style={styles.mainContent}>
        <DepartmentChart
          styles={styles}
          text={text}
          language={language}
          departmentStats={departmentStats}
          totalAcrossDepartments={totalAcrossDepartments}
          screenSize={screenSize}
        />

        <ActivityFeed
          styles={styles}
          text={text}
          language={language}
          recentActivities={recentActivities}
          screenSize={screenSize}
        />
      </div>

      <style>
        {`
          div[style*="overflowY: auto"]::-webkit-scrollbar {
            width: ${screenSize.isMobile ? "4px" : "6px"};
          }
          div[style*="overflowY: auto"]::-webkit-scrollbar-track {
            background: ${
              theme === "dark" ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.05)"
            };
            border-radius: 3px;
          }
          div[style*="overflowY: auto"]::-webkit-scrollbar-thumb {
            background: ${
              theme === "dark" ? "rgba(255,255,255,0.2)" : "rgba(0,0,0,0.2)"
            };
            border-radius: 3px;
          }
          div[style*="overflowY: auto"]::-webkit-scrollbar-thumb:hover {
            background: ${
              theme === "dark" ? "rgba(255,255,255,0.3)" : "rgba(0,0,0,0.3)"
            };
          }

          * {
            -webkit-tap-highlight-color: transparent;
          }

          @media (max-width: 768px) {
            * {
              text-rendering: optimizeSpeed;
            }
          }

          @media (-webkit-min-device-pixel-ratio: 2), (min-resolution: 192dpi) {
            * {
              text-rendering: optimizeLegibility;
            }
          }

          @media (prefers-reduced-motion: reduce) {
            * {
              animation-duration: 0.01ms !important;
              animation-iteration-count: 1 !important;
              transition-duration: 0.01ms !important;
            }
          }

          *:focus-visible {
            outline: 2px solid ${theme === "dark" ? "#3b82f6" : "#2563eb"};
            outline-offset: 2px;
            border-radius: 4px;
          }
        `}
      </style>
    </div>
  );
};

export default Dashboard;
